import { IcomoonIcon } from '@/components/atoms/IcomoonIcon'
import { useUser } from '@/contexts/AuthContext'
import { useOnClickOutside } from '@/hooks/useOnClickOutside'
import { isLatestEnterpriseOrTrial } from '@/lib/user'
import { css } from '@emotion/react'
import { useRef, useState } from 'react'
import { MyLabelDropdown } from './MyLabelDropdown'
import { MyLabel, MyLabelId } from '../../reducers/sourcingAnnotationCandidates'
import { Tooltip } from 'plume-ui'
import { COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS } from '../../reducers/column'
import { escape } from 'html-sloppy-escaper'
import { MyLabelItem } from '../atom/MyLabelItem'

type Props = {
  candidates: MyLabel[]
  myLabels: MyLabel[]
  position?: {
    rowIndex: number
    rowCount: number
    rowHeight: number
    minTableHeight: number
  }
  onSelectMyLabel: (id: MyLabelId) => void
  onDeselectMyLabel: (id: MyLabelId) => void
  onRequestEditModal: () => void
  disabled: boolean
}

const myLabelWidth = COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS.myLabels.width

export const MyLabelCell: React.FC<Props> = ({
  candidates,
  myLabels,
  position,
  onSelectMyLabel,
  onDeselectMyLabel,
  onRequestEditModal,
  disabled,
}) => {
  const [isOpenDropdown, setIsOpenDropdown] = useState(false)
  const cellRef = useRef<HTMLTableCellElement>(null)
  const handleOnClickCell = () => {
    if (disabled) return
    setIsOpenDropdown(true)
  }
  useOnClickOutside([cellRef], () => {
    if (isOpenDropdown) {
      setIsOpenDropdown(!isOpenDropdown)
    }
  })
  const user = useUser()
  const dropdownMaxHeight = 300
  const dropdownHeight = Math.min(
    candidates.length * 25 + 40,
    dropdownMaxHeight
  )
  let dropdownTop = undefined
  if (position) {
    const restRowsHeight =
      (position.rowCount - position.rowIndex) * position.rowHeight
    const restTableHeight =
      position.minTableHeight - (position.rowIndex + 1) * position.rowHeight
    const restHeight = Math.max(restRowsHeight, restTableHeight)
    if (restHeight < dropdownHeight) {
      dropdownTop = restHeight - dropdownHeight
    }
  }

  if (!isLatestEnterpriseOrTrial(user)) {
    return null
  }

  const myLabelIds = myLabels.map((it) => it.id)

  const selectedMyLabels = candidates.filter((myLabel) =>
    myLabelIds.includes(myLabel.id)
  )

  return (
    <td
      ref={cellRef}
      data-test-id="myLabel"
      onClick={handleOnClickCell}
      className={disabled ? 'disabled' : ''}
      css={css`
        min-width: ${myLabelWidth}px;
        max-width: ${myLabelWidth}px;
        white-space: break-spaces;
        &:hover {
          cursor: pointer;
          &::before {
            border-right: 1px solid rgb(217, 217, 217);
            border-left: 1px solid rgb(217, 217, 217);
          }
        }
        &::before {
          content: '';
          display: block;
          width: 100%;
          height: 100%;
          background-color: transparent;
          position: absolute;
          left: 0;
          top: 0;
          pointer-events: none;
        }
        &.disabled {
          cursor: not-allowed;
          opacity: 0.3;
        }
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
        `}
      >
        <MyLabelTooltip myLabels={selectedMyLabels}>
          <ul
            css={css`
              padding: 0;
              margin: 0;
              white-space: break-spaces;
              overflow: hidden;
              display: -webkit-box;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 2;
              > li {
                margin-right: 4px;
              }
            `}
          >
            {selectedMyLabels.map((myLabel) => (
              <MyLabelItem
                key={myLabel.id}
                myLabel={myLabel}
                maxWidth={`${myLabelWidth * 0.85}px`}
              />
            ))}
          </ul>
        </MyLabelTooltip>
        <IcomoonIcon
          css={css`
            font-size: 10px;
            display: block;
          `}
          name={'arrow1-down'}
        />
      </div>
      {isOpenDropdown ? (
        <MyLabelDropdown
          candidates={candidates}
          selectedMyLabelIds={myLabels.map((myLabel) => myLabel.id)}
          maxHeight={dropdownMaxHeight}
          position={{ top: dropdownTop, left: 8 }}
          onSelectMyLabel={onSelectMyLabel}
          onDeselectMyLabel={onDeselectMyLabel}
          onRequestEditModal={onRequestEditModal}
        />
      ) : null}
    </td>
  )
}

type MyLabelTooltipProps = {
  myLabels: MyLabel[]
  children: React.ReactNode
}

const MyLabelTooltip: React.FC<MyLabelTooltipProps> = ({
  myLabels,
  children,
}) => {
  const label = myLabels
    .map((it) => it.name)
    .map(escape)
    .join('<br>')
  return (
    <Tooltip label={label} data-html>
      {children}
    </Tooltip>
  )
}
